<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { AvKind } from '../lib/avDevices'
import { useAv } from '../stores/av'
import { useSession } from '../stores/session'

/** `compact`: just the pickers (inside a call); otherwise also the checks. */
defineProps<{ compact?: boolean }>()
const av = useAv()
const session = useSession()

onMounted(() => void av.refresh())

async function allow(): Promise<void> {
  try {
    await av.requestAccess()
  } catch {
    session.toast('Camera and microphone access was blocked. Allow it in your browser’s site settings.', 'error')
  }
}

function pick(kind: AvKind, e: Event): void {
  void av.choose(kind, (e.target as HTMLSelectElement).value)
}

// --- Mic check: a segmented level meter fed by the chosen microphone. ---
const SEGMENTS = 24
const level = ref(0)
const testingMic = ref(false)
let micStream: MediaStream | null = null
let micCtx: AudioContext | null = null
let raf = 0

function stopMic(): void {
  cancelAnimationFrame(raf)
  micStream?.getTracks().forEach((t) => t.stop())
  void micCtx?.close().catch(() => undefined)
  micStream = null
  micCtx = null
  level.value = 0
}

async function startMic(): Promise<void> {
  stopMic()
  const id = av.current('audioinput')
  micStream = await navigator.mediaDevices.getUserMedia({ audio: id ? { deviceId: { exact: id } } : true })
  micCtx = new AudioContext()
  // Created after an await, so outside the click: browsers may start it suspended.
  await micCtx.resume().catch(() => undefined)
  const analyser = micCtx.createAnalyser()
  analyser.fftSize = 512
  micCtx.createMediaStreamSource(micStream).connect(analyser)
  const buf = new Float32Array(analyser.fftSize)
  const tick = () => {
    analyser.getFloatTimeDomainData(buf)
    let sum = 0
    for (const v of buf) sum += v * v
    // RMS in decibels, -60..0 dB onto 0..1; the peak falls back slowly so it's readable.
    const db = 20 * Math.log10(Math.sqrt(sum / buf.length) || 1e-6)
    level.value = Math.max(Math.min(1, Math.max(0, (db + 60) / 60)), level.value * 0.94)
    raf = requestAnimationFrame(tick)
  }
  tick()
}

async function toggleMicTest(): Promise<void> {
  testingMic.value = !testingMic.value
  if (!testingMic.value) return stopMic()
  try {
    await startMic()
  } catch {
    testingMic.value = false
    session.toast('Couldn’t open that microphone', 'error')
  }
}

// --- Camera check: a live preview of the chosen camera. ---
const preview = ref<HTMLVideoElement | null>(null)
const testingCam = ref(false)
let camStream: MediaStream | null = null

function stopCam(): void {
  camStream?.getTracks().forEach((t) => t.stop())
  camStream = null
}

async function startCam(): Promise<void> {
  stopCam()
  const id = av.current('videoinput')
  camStream = await navigator.mediaDevices.getUserMedia({ video: id ? { deviceId: { exact: id } } : true })
  if (preview.value) preview.value.srcObject = camStream
}

async function toggleCamTest(): Promise<void> {
  testingCam.value = !testingCam.value
  if (!testingCam.value) return stopCam()
  try {
    await startCam()
  } catch {
    testingCam.value = false
    session.toast('Couldn’t open that camera', 'error')
  }
}
watch(preview, (v) => {
  if (v && camStream) v.srcObject = camStream
})

// Switching device while a check runs restarts it on the new one.
watch(
  () => av.current('audioinput'),
  () => testingMic.value && void startMic().catch(() => (testingMic.value = false)),
)
watch(
  () => av.current('videoinput'),
  () => testingCam.value && void startCam().catch(() => (testingCam.value = false)),
)

// --- Speaker check: a short two-note chime on the chosen output. ---
const chiming = ref(false)
async function chime(): Promise<void> {
  chiming.value = true
  const ctx = new AudioContext()
  try {
    const dest = ctx.createMediaStreamDestination()
    const el = new Audio() as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }
    el.srcObject = dest.stream
    const out = av.current('audiooutput')
    if (out && el.setSinkId) await el.setSinkId(out)
    await el.play()
    for (const [i, f] of [660, 990].entries()) {
      const t = ctx.currentTime + i * 0.22
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.frequency.value = f
      gain.gain.setValueAtTime(0.0001, t)
      gain.gain.exponentialRampToValueAtTime(0.3, t + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.4)
      osc.connect(gain).connect(dest)
      osc.start(t)
      osc.stop(t + 0.42)
    }
    await new Promise((r) => setTimeout(r, 800))
    el.pause()
  } catch {
    session.toast('Couldn’t play on that speaker', 'error')
  } finally {
    void ctx.close().catch(() => undefined)
    chiming.value = false
  }
}

onBeforeUnmount(() => {
  stopMic()
  stopCam()
})

const lit = computed(() => Math.round(level.value * SEGMENTS))
</script>

<template>
  <div class="space-y-4" data-testid="av-settings">
    <div v-if="!av.permitted" class="flex items-center justify-between gap-3 border border-border p-3">
      <p class="text-xs text-text-muted">To choose devices, let hrth see your microphone and camera.</p>
      <button class="btn-outline shrink-0" data-testid="av-allow" @click="allow">Allow</button>
    </div>

    <div>
      <label class="label" for="av-mic">Microphone</label>
      <div class="flex gap-2">
        <select
          id="av-mic"
          class="input"
          :value="av.current('audioinput')"
          :disabled="!av.mics.length"
          data-testid="av-audioinput"
          @change="pick('audioinput', $event)"
        >
          <option v-if="!av.mics.length" value="">Default</option>
          <option v-for="d in av.mics" :key="d.id" :value="d.id">{{ d.label }}</option>
        </select>
        <button v-if="!compact" class="btn-outline shrink-0" :aria-pressed="testingMic" data-testid="av-test-mic" @click="toggleMicTest">
          {{ testingMic ? 'Stop' : 'Test' }}
        </button>
      </div>
      <div v-if="testingMic" class="mt-2 flex h-3 gap-px" data-testid="av-mic-level" :data-level="lit">
        <span v-for="i in SEGMENTS" :key="i" class="flex-1" :class="i <= lit ? (i > SEGMENTS - 3 ? 'bg-signal' : 'bg-voice') : 'bg-border'" />
      </div>
      <p v-if="!compact" class="mt-1 text-xs text-text-muted">
        Using Bluetooth headphones' own microphone switches them to call quality, for all sound, until the call ends. Another
        microphone keeps music sounding right.
      </p>
    </div>

    <div>
      <label class="label" for="av-cam">Camera</label>
      <div class="flex gap-2">
        <select
          id="av-cam"
          class="input"
          :value="av.current('videoinput')"
          :disabled="!av.cameras.length"
          data-testid="av-videoinput"
          @change="pick('videoinput', $event)"
        >
          <option v-if="!av.cameras.length" value="">{{ av.permitted ? 'No camera found' : 'Default' }}</option>
          <option v-for="d in av.cameras" :key="d.id" :value="d.id">{{ d.label }}</option>
        </select>
        <button
          v-if="!compact"
          class="btn-outline shrink-0"
          :aria-pressed="testingCam"
          :disabled="!av.cameras.length && av.permitted"
          data-testid="av-test-cam"
          @click="toggleCamTest"
        >
          {{ testingCam ? 'Stop' : 'Preview' }}
        </button>
      </div>
      <video
        v-if="testingCam"
        ref="preview"
        autoplay
        playsinline
        muted
        class="mt-2 aspect-video w-full -scale-x-100 border border-border bg-black object-cover"
        data-testid="av-cam-preview"
      />
    </div>

    <div>
      <label class="label" for="av-spk">Speaker</label>
      <div v-if="!av.outputSelectable" class="text-xs text-text-muted">This browser plays calls through your system’s output.</div>
      <div v-else class="flex gap-2">
        <button v-if="av.outputPrompt" class="btn-outline flex-1" data-testid="av-pick-speaker" @click="av.promptForSpeaker()">
          Choose speaker…
        </button>
        <select
          v-else
          id="av-spk"
          class="input"
          :value="av.current('audiooutput')"
          :disabled="!av.speakers.length"
          data-testid="av-audiooutput"
          @change="pick('audiooutput', $event)"
        >
          <option v-if="!av.speakers.length" value="">Default</option>
          <option v-for="d in av.speakers" :key="d.id" :value="d.id">{{ d.label }}</option>
        </select>
        <button v-if="!compact" class="btn-outline shrink-0" :disabled="chiming" data-testid="av-test-speaker" @click="chime">
          {{ chiming ? 'Playing' : 'Play' }}
        </button>
      </div>
    </div>
  </div>
</template>
